import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, AlertTriangle, CheckCircle2, Loader2, ShieldCheck, RefreshCw, Database } from 'lucide-react';
import { BACKEND_URL } from '../config/api';

/**
 * Production Launch Migration UI.
 *
 * One-screen tool to:
 *   1. Run dry-run /api/admin/launch/preview-diff
 *   2. Show the diff in human-readable form
 *   3. Apply with explicit confirmation /api/admin/launch/apply
 *
 * Designed so the operator never has to touch a terminal.
 */
export default function AdminLaunchMigration() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        if (data.user_type !== 'admin') {
          navigate('/login');
          return;
        }
        setUser(data);
      } catch {
        navigate('/login');
      } finally {
        setLoadingUser(false);
      }
    })();
  }, [navigate]);

  const fetchDiff = async () => {
    setDiffLoading(true);
    setApplyResult(null);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/launch/preview-diff`, { withCredentials: true });
      setDiff(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch dry-run diff');
    } finally {
      setDiffLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchDiff();
  }, [user]);

  const apply = async () => {
    if (!confirmChecked) {
      toast.error('Tick the confirmation checkbox first');
      return;
    }
    setApplying(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/launch/apply`,
        { confirm: true },
        { withCredentials: true },
      );
      setApplyResult(data);
      if (data.idempotent_ok) {
        toast.success('Migration applied — production aligned with launch config');
      } else {
        toast.warning('Applied with residual diff. Inspect output below.');
      }
      // Refresh diff to confirm
      fetchDiff();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  if (loadingUser) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const totalGroups = diff?.total_change_groups ?? 0;
  const isAligned = totalGroups === 0;
  const protectedCols = diff?.protected_collections_not_touched || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/dashboard')} className="text-slate-600 hover:text-slate-900" data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <Database className="h-6 w-6 text-orange-500" />
            <h1 className="text-xl font-bold text-slate-900">Production Launch Migration</h1>
          </div>
          <span className="text-sm text-slate-600" data-testid="user-name">Hi, {user?.name}</span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5" data-testid="info-banner">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold mb-1">Safe, idempotent, config-only migration</p>
              <p className="leading-relaxed">
                This tool aligns subscription plans, credit costs, top-up packages and Stripe Price IDs to the launch
                spec. A full backup is written to the <code className="bg-amber-100 px-1 rounded">config_backups</code> collection
                <strong> before</strong> any change. It will <strong>never</strong> touch user data, chats, bookings,
                payments, subscriptions, credits, referrals, or profiles.
              </p>
            </div>
          </div>
        </div>

        {/* Dry-run section */}
        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="dry-run-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">1. Dry-run diff (no writes)</h2>
            <button
              onClick={fetchDiff}
              disabled={diffLoading}
              className="text-sm flex items-center gap-2 px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50"
              data-testid="refresh-diff-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${diffLoading ? 'animate-spin' : ''}`} />
              Re-run dry-run
            </button>
          </div>

          {diffLoading && !diff ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Fetching current state from production database…
            </div>
          ) : diff ? (
            <>
              <div className={`rounded-lg p-4 mb-4 ${isAligned ? 'bg-emerald-50 border border-emerald-200' : 'bg-orange-50 border border-orange-200'}`}>
                <p className={`flex items-center gap-2 font-medium ${isAligned ? 'text-emerald-800' : 'text-orange-800'}`}>
                  {isAligned ? <CheckCircle2 className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
                  {isAligned
                    ? 'Database already matches launch config — no changes needed.'
                    : `${totalGroups} configuration group${totalGroups === 1 ? '' : 's'} would be updated.`}
                </p>
              </div>

              {!isAligned && (
                <div className="space-y-3">
                  {Object.entries(diff.changes || {}).map(([group, detail]) => (
                    <div key={group} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`diff-group-${group}`}>
                      <div className="bg-slate-50 px-4 py-2 font-medium text-slate-800 text-sm">{group}</div>
                      <div className="p-4 text-xs">
                        <pre className="whitespace-pre-wrap break-all bg-slate-900 text-slate-100 p-3 rounded overflow-x-auto" style={{ maxHeight: 320 }}>
                          {JSON.stringify(detail, null, 2)}
                        </pre>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Protected collections list */}
              {protectedCols.length > 0 && (
                <div className="mt-4 text-xs text-slate-500">
                  <p className="font-medium mb-1">Collections that will NOT be touched:</p>
                  <p className="leading-relaxed">{protectedCols.join(' · ')}</p>
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Apply section */}
        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="apply-section">
          <h2 className="text-lg font-semibold text-slate-900 mb-3">2. Apply</h2>

          {isAligned ? (
            <p className="text-sm text-slate-500">No action needed — database is already aligned.</p>
          ) : (
            <>
              <label className="flex items-start gap-3 mb-4 cursor-pointer" data-testid="confirm-checkbox-label">
                <input
                  type="checkbox"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                  className="mt-0.5"
                  data-testid="confirm-checkbox"
                />
                <span className="text-sm text-slate-700">
                  I have reviewed the dry-run above and authorise the migration to write the listed changes to this
                  environment's database. A backup will be saved automatically.
                </span>
              </label>

              <button
                onClick={apply}
                disabled={!confirmChecked || applying}
                className="px-5 py-2.5 rounded-lg font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                data-testid="apply-btn"
              >
                {applying ? <><Loader2 className="h-4 w-4 animate-spin" /> Applying…</> : 'Confirm & Apply'}
              </button>
            </>
          )}
        </div>

        {/* Apply result */}
        {applyResult && (
          <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="apply-result">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">Apply result</h2>
            <div className={`rounded-lg p-4 mb-4 ${applyResult.idempotent_ok ? 'bg-emerald-50 border border-emerald-200' : 'bg-orange-50 border border-orange-200'}`}>
              <p className={`flex items-center gap-2 font-medium ${applyResult.idempotent_ok ? 'text-emerald-800' : 'text-orange-800'}`}>
                {applyResult.idempotent_ok ? <CheckCircle2 className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
                {applyResult.note}
              </p>
              <p className="text-xs text-slate-600 mt-2">
                Backup ID: <code className="bg-slate-100 px-1 rounded">{applyResult.backup_id}</code>
              </p>
            </div>
            <details className="text-xs">
              <summary className="cursor-pointer font-medium text-slate-700 mb-2">Full server response</summary>
              <pre className="whitespace-pre-wrap break-all bg-slate-900 text-slate-100 p-3 rounded overflow-x-auto" style={{ maxHeight: 480 }}>
                {JSON.stringify(applyResult, null, 2)}
              </pre>
            </details>
          </div>
        )}

        {/* Public verification quick-links */}
        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="verify-section">
          <h2 className="text-lg font-semibold text-slate-900 mb-3">3. Verify (public endpoints)</h2>
          <p className="text-sm text-slate-600 mb-3">
            After applying, click each link to confirm production reflects the launch config.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
            <a className="px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700"
               href="/api/subscription-plans" target="_blank" rel="noopener noreferrer" data-testid="verify-plans-link">
              /api/subscription-plans
            </a>
            <a className="px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700"
               href="/api/credits/costs" target="_blank" rel="noopener noreferrer" data-testid="verify-costs-link">
              /api/credits/costs
            </a>
            <a className="px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700"
               href="/api/settings/public" target="_blank" rel="noopener noreferrer" data-testid="verify-public-link">
              /api/settings/public
            </a>
          </div>
        </div>

        {/* Duplicate start_chat refund repair */}
        <RepairDuplicateStartChatPanel />

        {/* No-subscription deduction repair */}
        <RepairNoSubDeductionsPanel />

        {/* Region backfill — out-of-region signups */}
        <RegionBackfillPanel />
      </div>
    </div>
  );
}


function RepairDuplicateStartChatPanel() {
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [applying, setApplying] = React.useState(false);
  const [applyResult, setApplyResult] = React.useState(null);
  const [confirmed, setConfirmed] = React.useState(false);

  const runPreview = async () => {
    setLoading(true);
    setApplyResult(null);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/launch/repair/duplicate-start-chat-preview`,
        { withCredentials: true },
      );
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dry-run failed');
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!confirmed) {
      toast.error('Tick the confirmation checkbox first');
      return;
    }
    setApplying(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/launch/repair/duplicate-start-chat-apply`,
        { confirm: true },
        { withCredentials: true },
      );
      setApplyResult(data);
      toast.success(`Refunded ${data.total_credits_refunded} credits across ${data.employers_affected} employer(s)`);
      runPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="repair-section">
      <h2 className="text-lg font-semibold text-slate-900 mb-3">4. Repair duplicate start-chat deductions</h2>
      <p className="text-sm text-slate-600 mb-3">
        One-off: refund employers who were double-charged for starting a chat with the same tradesperson before the idempotency fix.
        The earliest debit is kept as legitimate; every duplicate is refunded. Idempotent — safe to re-run.
      </p>
      <button
        onClick={runPreview}
        disabled={loading}
        className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 mb-3"
        data-testid="repair-preview-btn"
      >
        {loading ? 'Scanning…' : 'Scan for duplicate deductions'}
      </button>

      {preview && (
        <>
          <div className={`rounded-lg p-4 mb-3 ${preview.total_employers_affected === 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-orange-50 border border-orange-200'}`}>
            <p className={`font-medium ${preview.total_employers_affected === 0 ? 'text-emerald-800' : 'text-orange-800'}`}>
              {preview.total_employers_affected === 0
                ? 'No duplicate start-chat charges found.'
                : `Found ${preview.total_duplicate_transactions} duplicate charge${preview.total_duplicate_transactions === 1 ? '' : 's'} across ${preview.total_employers_affected} employer${preview.total_employers_affected === 1 ? '' : 's'} — ${preview.total_credits_to_refund} credits would be refunded.`}
            </p>
          </div>
          {preview.total_employers_affected > 0 && (
            <>
              <details className="text-xs mb-3">
                <summary className="cursor-pointer font-medium text-slate-700">Full breakdown</summary>
                <pre className="whitespace-pre-wrap break-all bg-slate-900 text-slate-100 p-3 rounded mt-2 overflow-x-auto" style={{ maxHeight: 320 }}>
                  {JSON.stringify(preview.details, null, 2)}
                </pre>
              </details>
              <label className="flex items-start gap-3 mb-3 cursor-pointer">
                <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5" data-testid="repair-confirm" />
                <span className="text-sm text-slate-700">I authorise refunding the listed duplicate deductions.</span>
              </label>
              <button
                onClick={apply}
                disabled={!confirmed || applying}
                className="px-5 py-2.5 rounded-lg font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:bg-slate-300"
                data-testid="repair-apply-btn"
              >
                {applying ? 'Refunding…' : 'Refund duplicate deductions'}
              </button>
            </>
          )}
        </>
      )}

      {applyResult && (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="repair-apply-result">
          <p className="font-medium text-emerald-800 mb-2">
            Refunded {applyResult.total_credits_refunded} credit(s) across {applyResult.employers_affected} employer(s).
          </p>
          <details className="text-xs">
            <summary className="cursor-pointer font-medium text-slate-700">Refund receipts</summary>
            <pre className="whitespace-pre-wrap break-all bg-slate-900 text-slate-100 p-3 rounded mt-2 overflow-x-auto" style={{ maxHeight: 320 }}>
              {JSON.stringify(applyResult, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}


function RepairNoSubDeductionsPanel() {
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [applying, setApplying] = React.useState(false);
  const [applyResult, setApplyResult] = React.useState(null);
  const [confirmed, setConfirmed] = React.useState(false);

  const runPreview = async () => {
    setLoading(true);
    setApplyResult(null);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/launch/repair/no-sub-deductions-preview`,
        { withCredentials: true },
      );
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dry-run failed');
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!confirmed) {
      toast.error('Tick the confirmation checkbox first');
      return;
    }
    setApplying(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/launch/repair/no-sub-deductions-apply`,
        { confirm: true },
        { withCredentials: true },
      );
      setApplyResult(data);
      toast.success(`Refunded ${data.total_credits_refunded} credits across ${data.employers_affected} employer(s)`);
      // Refresh preview — must show 0 affected (idempotency proof).
      runPreview();
      setConfirmed(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="repair-no-sub-section">
      <h2 className="text-lg font-semibold text-slate-900 mb-3">5. Repair no-subscription deductions</h2>
      <p className="text-sm text-slate-600 mb-3">
        One-off: refund employers who lost credits (start chat, profile unlock, view availability)
        while they had <strong>no active subscription</strong>. This existed before subscription gating
        was added. The original transaction is flagged <code className="bg-slate-100 px-1 rounded">refunded:true</code>,
        so re-running this refund cannot refund the same debit twice. Idempotent — safe to re-run.
      </p>
      <button
        onClick={runPreview}
        disabled={loading}
        className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 mb-3"
        data-testid="repair-no-sub-preview-btn"
      >
        {loading ? 'Scanning…' : 'Scan for no-subscription deductions'}
      </button>

      {preview && (
        <>
          <div className={`rounded-lg p-4 mb-3 ${preview.total_employers_affected === 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-orange-50 border border-orange-200'}`} data-testid="repair-no-sub-preview-result">
            <p className={`font-medium ${preview.total_employers_affected === 0 ? 'text-emerald-800' : 'text-orange-800'}`}>
              {preview.total_employers_affected === 0
                ? 'No deductions found that were made without an active subscription.'
                : `Found ${preview.total_transactions} debit${preview.total_transactions === 1 ? '' : 's'} across ${preview.total_employers_affected} employer${preview.total_employers_affected === 1 ? '' : 's'} — ${preview.total_credits_to_refund} credits would be refunded.`}
            </p>
          </div>
          {preview.total_employers_affected > 0 && (
            <>
              <details className="text-xs mb-3">
                <summary className="cursor-pointer font-medium text-slate-700">Full breakdown</summary>
                <pre className="whitespace-pre-wrap break-all bg-slate-900 text-slate-100 p-3 rounded mt-2 overflow-x-auto" style={{ maxHeight: 320 }}>
                  {JSON.stringify(preview.details, null, 2)}
                </pre>
              </details>
              <label className="flex items-start gap-3 mb-3 cursor-pointer" data-testid="repair-no-sub-confirm-label">
                <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5" data-testid="repair-no-sub-confirm" />
                <span className="text-sm text-slate-700">
                  I authorise refunding the listed deductions. Each transaction is flagged refunded once and cannot be refunded again.
                </span>
              </label>
              <button
                onClick={apply}
                disabled={!confirmed || applying}
                className="px-5 py-2.5 rounded-lg font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:bg-slate-300"
                data-testid="repair-no-sub-apply-btn"
              >
                {applying ? 'Refunding…' : 'Refund no-subscription deductions'}
              </button>
            </>
          )}
        </>
      )}

      {applyResult && (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="repair-no-sub-apply-result">
          <p className="font-medium text-emerald-800 mb-2">
            Refunded {applyResult.total_credits_refunded} credit(s) across {applyResult.employers_affected} employer(s).
          </p>
          <details className="text-xs">
            <summary className="cursor-pointer font-medium text-slate-700">Refund receipts</summary>
            <pre className="whitespace-pre-wrap break-all bg-slate-900 text-slate-100 p-3 rounded mt-2 overflow-x-auto" style={{ maxHeight: 320 }}>
              {JSON.stringify(applyResult, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}


function RegionBackfillPanel() {
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [applying, setApplying] = React.useState(false);
  const [applyResult, setApplyResult] = React.useState(null);
  const [confirmed, setConfirmed] = React.useState(false);

  const runPreview = async () => {
    setLoading(true);
    setApplyResult(null);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/expansion/backfill-users-preview`,
        {},
        { withCredentials: true },
      );
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dry-run failed');
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!confirmed) {
      toast.error('Tick the confirmation checkbox first');
      return;
    }
    setApplying(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/expansion/backfill-users-apply`,
        { confirm: true },
        { withCredentials: true },
      );
      setApplyResult(data);
      toast.success(`Backfilled ${data.users_updated} users · seeded ${data.regions_seeded.length} expansion region(s)`);
      runPreview();
      setConfirmed(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed');
    } finally {
      setApplying(false);
    }
  };

  const seedCities = preview?.would_seed_cities || {};
  const seedCityNames = Object.keys(seedCities);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="region-backfill-section">
      <h2 className="text-lg font-semibold text-slate-900 mb-3">6. Region backfill — out-of-region signups</h2>
      <p className="text-sm text-slate-600 mb-3">
        Scans every signed-up user, classifies them by postcode/town, and stamps
        <code className="bg-slate-100 px-1 rounded mx-1">region_kind</code>,
        <code className="bg-slate-100 px-1 rounded mx-1">matched_region_id</code>,
        and <code className="bg-slate-100 px-1 rounded mx-1">mapped_region_name</code> on the user.
        Auto-creates <strong>expansion</strong> regions (Edinburgh, Manchester, Birmingham, Glasgow, etc.)
        for cities with at least one out-of-region signup. Never marks a city as live.
        Idempotent — safe to re-run. Existing good data is never overwritten with "unknown".
      </p>
      <button
        onClick={runPreview}
        disabled={loading}
        className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 mb-3"
        data-testid="region-backfill-preview-btn"
      >
        {loading ? 'Scanning users…' : 'Scan & dry-run'}
      </button>

      {preview && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3" data-testid="region-backfill-preview-result">
            <Stat label="Scanned" value={preview.total_scanned} tone="slate" />
            <Stat label="Live" value={preview.summary.live} tone="emerald" />
            <Stat label="Priority" value={preview.summary.priority} tone="amber" />
            <Stat label="Expansion" value={preview.summary.expansion} tone="orange" />
            <Stat label="Unmapped" value={preview.summary.unmapped} tone="slate" />
          </div>
          <div className="text-xs text-slate-600 mb-3">
            Pending changes: <strong>{preview.summary.users_with_pending_changes}</strong>
            {' · '}Existing good data preserved: <strong>{preview.summary.no_overwrite_kept_existing}</strong>
          </div>

          {seedCityNames.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3">
              <p className="text-sm font-semibold text-orange-900 mb-2">
                Will create {seedCityNames.length} expansion region(s):
              </p>
              <ul className="text-xs text-orange-800 space-y-0.5">
                {seedCityNames.map(name => (
                  <li key={name}>
                    <strong>{name}</strong> ({seedCities[name].country}) —
                    {' '}{seedCities[name].user_count} user{seedCities[name].user_count === 1 ? '' : 's'},
                    {' '}prefixes: {seedCities[name].prefixes.join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {preview.samples?.length > 0 && (
            <details className="text-xs mb-3">
              <summary className="cursor-pointer font-medium text-slate-700">Sample before/after ({preview.samples.length} of {preview.summary.users_with_pending_changes})</summary>
              <pre className="whitespace-pre-wrap break-all bg-slate-900 text-slate-100 p-3 rounded mt-2 overflow-x-auto" style={{ maxHeight: 320 }}>
                {JSON.stringify(preview.samples, null, 2)}
              </pre>
            </details>
          )}

          {(preview.summary.users_with_pending_changes > 0 || seedCityNames.length > 0) ? (
            <>
              <label className="flex items-start gap-3 mb-3 cursor-pointer" data-testid="region-backfill-confirm-label">
                <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5" data-testid="region-backfill-confirm" />
                <span className="text-sm text-slate-700">
                  I authorise stamping region classification on the listed users and creating the listed expansion regions (status: early_interest, never live).
                </span>
              </label>
              <button
                onClick={apply}
                disabled={!confirmed || applying}
                className="px-5 py-2.5 rounded-lg font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:bg-slate-300"
                data-testid="region-backfill-apply-btn"
              >
                {applying ? 'Backfilling…' : 'Apply backfill'}
              </button>
            </>
          ) : (
            <p className="text-sm text-emerald-700 font-medium" data-testid="region-backfill-clean">
              All users already classified — nothing to do.
            </p>
          )}
        </>
      )}

      {applyResult && (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="region-backfill-apply-result">
          <p className="font-medium text-emerald-800 mb-2">
            Backfill complete — {applyResult.users_updated} user(s) updated,
            {' '}{applyResult.regions_seeded.length} expansion region(s) seeded.
          </p>
          <div className="text-xs text-emerald-800">
            Final breakdown:
            {' live='}{applyResult.final_breakdown.live}
            {' · priority='}{applyResult.final_breakdown.priority}
            {' · expansion='}{applyResult.final_breakdown.expansion}
            {' · unmapped='}{applyResult.final_breakdown.unmapped}
            {' · pending='}{applyResult.remaining_pending_changes_after_apply}
          </div>
          <details className="text-xs mt-2">
            <summary className="cursor-pointer font-medium text-slate-700">Full server response</summary>
            <pre className="whitespace-pre-wrap break-all bg-slate-900 text-slate-100 p-3 rounded mt-2 overflow-x-auto" style={{ maxHeight: 320 }}>
              {JSON.stringify(applyResult, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    amber: 'bg-amber-50 text-amber-900 border-amber-200',
    orange: 'bg-orange-50 text-orange-900 border-orange-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
  };
  return (
    <div className={`rounded-lg border p-2 text-center ${tones[tone] || tones.slate}`}>
      <div className="text-xs uppercase tracking-wide opacity-75">{label}</div>
      <div className="text-xl font-bold">{value}</div>
    </div>
  );
}

